import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Icon } from '@iconify/react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { ApiClientError } from '@/lib/api/client'
import type { AvatarConfig } from '@/features/donorAvatar/avatar-catalog'
import type { MascotConfig } from '@/features/donorMascot/mascot-catalog'
import type { LandingAchievementId } from '@/lib/landing-achievements'
import { onlyDigits } from '@/components/CpfCnpjInput'
import { fetchLandingCampaign } from '@/lib/landing'
import { initializeMetaPixel } from '@/lib/meta-pixel'
import {
  landingChangeDonorPassword,
  landingDonorLogout,
  landingFetchCampaignLuckyNumbers,
  landingFetchDonorMe,
  landingUpdateDonorProfile,
  type LandingDonor,
  type LandingLuckyNumber,
} from '@/lib/landing-auth'
import {
  clearLandingDonorSessionIfUnauthorized,
  writeLandingDonorToken,
} from '@/lib/landing-donor-session'
import {
  fetchPublicCampaignRoulette,
  landingFetchRouletteHistory,
  landingRouletteGetSpins,
  type LandingRouletteHistoryItem,
  type LandingRouletteSpinsResponse,
  type LandingSpinResult,
  type PublicRouletteDisplay,
} from '@/lib/landing-roulette'
import { buildPendingCouponFromSpinResult } from '@/lib/landing-coupon'
import { LandingScratchPrizes } from './LandingScratchPrizes'
import { usePurchaseScratch } from '@/hooks/usePurchaseScratch'
import { PurchaseScratchModal } from './PurchaseScratchModal'
import { useLandingCampaignWidget } from '@/hooks/useLandingCampaignWidget'
import { isCampaignOpenForSales } from '@/lib/campaign-sales'
import { resolveCampaignHomeHref } from '@/lib/campaign-view-page-url'
import { LandingSalesClosedNotice } from '@/components/Landing/LandingSalesClosedNotice'
import type {
  LandingCampaign,
  LandingCampaignResponse,
  LandingInstitution,
} from '@/types/landing'
import {
  buildLandingInstitutionName,
  resolveLandingAccountBackdropUrl,
} from '@/lib/landing-account-visual'
import purchaseButtonStyles from '@/pages/LandingAccountPage/LandingAccountPage.module.css'
import { LandingProfileCadastro, LandingProfileSecurity } from './LandingProfileForms'
import { LandingProfileIdentity } from './LandingProfileIdentity'
import { LandingProfileAchievements } from './LandingProfileAchievements'
import { LandingProfileQuestionnaire } from './LandingProfileQuestionnaire'
import { LandingRouletteClaims } from './LandingRouletteClaims'
import { LandingProfileNumbers } from './LandingProfileNumbers'
import { LandingProfileRoulette } from './LandingProfileRoulette'
import { LandingProfileShell } from './LandingProfileShell'
import { LandingProfileSkeleton } from './LandingProfileSkeleton'
import { LandingProfileTopDonors } from './LandingProfileTopDonors'
import { LandingProfileWorkspace } from './LandingProfileWorkspace'
import { getRouletteOpportunityCount } from './landing-profile-dock'
import { ParticipantShareButton } from '@/features/participantShare/ParticipantShareButton'
import {
  buildProfileFormState,
  birthDateToIso,
  createEmptyPasswordForm,
  flattenApiError,
  getProfileCompletionRate,
  isDonationOnlyCampaign,
  type PasswordFormState,
  type ProfileFormState,
} from './landing-profile-format'
import styles from './LandingNumbersPage.module.css'
import { groupLuckyNumbersByPurchase } from './landing-profile-purchases'

export function LandingNumbersPage() {
  const navigate = useNavigate()
  const { slug } = useParams<{ slug: string }>()
  const [searchParams] = useSearchParams()
  const [data, setData] = useState<LandingCampaignResponse | null>(null)
  const [donor, setDonor] = useState<LandingDonor | null>(null)
  const [numbers, setNumbers] = useState<LandingLuckyNumber[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isLoggingOut, setIsLoggingOut] = useState(false)
  const [logoutError, setLogoutError] = useState<string | null>(null)
  const [profileForm, setProfileForm] = useState<ProfileFormState | null>(null)
  const [passwordForm, setPasswordForm] = useState<PasswordFormState>(
    createEmptyPasswordForm
  )
  const [profileError, setProfileError] = useState<string | null>(null)
  const [profileSuccess, setProfileSuccess] = useState<string | null>(null)
  const [securityError, setSecurityError] = useState<string | null>(null)
  const [securitySuccess, setSecuritySuccess] = useState<string | null>(null)
  const [numbersQuery, setNumbersQuery] = useState('')
  const [achievementsRefreshKey, setAchievementsRefreshKey] = useState(0)
  const [purchaseScratchRefreshKey, setPurchaseScratchRefreshKey] = useState(0)
  const pendingScratchRefresh = useRef(false)
  const [achievementsReady, setAchievementsReady] = useState(false)
  const [rankingRefreshKey, setRankingRefreshKey] = useState(0)
  const [avatarOpenSignal, setAvatarOpenSignal] = useState(0)
  const [avatarBadgeUnlocked, setAvatarBadgeUnlocked] = useState(false)
  const [avatarBadgeEnabled, setAvatarBadgeEnabled] = useState(false)
  const handleAvatarBadgeStatus = useCallback((unlocked: boolean, enabled: boolean) => {
    setAvatarBadgeUnlocked(unlocked)
    setAvatarBadgeEnabled(enabled)
  }, [])
  const [earnedBadges, setEarnedBadges] = useState<{
    campaignId: number
    ids: LandingAchievementId[]
  } | null>(null)
  const [avatarDetailsSignal, setAvatarDetailsSignal] = useState(0)
  const [isSavingProfile, setIsSavingProfile] = useState(false)
  const [isSavingPassword, setIsSavingPassword] = useState(false)
  const [publicRoulette, setPublicRoulette] = useState<
    PublicRouletteDisplay | null | undefined
  >(undefined)
  const [rouletteBalance, setRouletteBalance] = useState<{
    campaignId: number
    donorId: number
    value: LandingRouletteSpinsResponse | null
    error?: string
  } | null>(null)
  const [rouletteHistory, setRouletteHistory] = useState<LandingRouletteHistoryItem[]>([])
  const [spinHistory, setSpinHistory] = useState<{
    campaignId: number
    donorId: number
    history: LandingRouletteHistoryItem[]
  } | null>(null)
  const [isLoadingHistory, setIsLoadingHistory] = useState(false)
  const [showOpportunityModal, setShowOpportunityModal] = useState(false)
  const rouletteBalanceRequestRef = useRef(0)

  // A storage flag is only a hint; /me is the authority even after a reload
  // or when the mobile browser blocks localStorage.
  const donorToken = 'cookie'
  const affiliateCode = searchParams.get('af')?.trim() || undefined
  const focusRouleta = searchParams.get('focus') === 'roleta'
  const phonePrefill = searchParams.get('phone')?.trim() || undefined

  const redirectToAccount = useCallback(() => {
    if (!slug) return
    const params = new URLSearchParams()
    params.set('return', 'account')
    if (affiliateCode) params.set('af', affiliateCode)
    if (phonePrefill) params.set('phone', phonePrefill)
    navigate(`/campanha/${slug}/conta?${params.toString()}`, { replace: true })
  }, [affiliateCode, navigate, phonePrefill, slug])

  const handleLogout = useCallback(async () => {
    if (!slug || isLoggingOut) return
    setIsLoggingOut(true)
    setLogoutError(null)

    try {
      await landingDonorLogout()
    } catch (err) {
      if (!(err instanceof ApiClientError && err.status === 401)) {
        setLogoutError('Não foi possível sair da conta. Tente novamente.')
        setIsLoggingOut(false)
        return
      }
    }

    writeLandingDonorToken(null)
    navigate(`/campanha/${slug}/conta?signed_out=1`, { replace: true })
  }, [isLoggingOut, navigate, slug])

  const loadAccountContext = useCallback(async () => {
    if (!slug) {
      setError('Campanha inválida.')
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const response = await fetchLandingCampaign(slug)
      const landingPayload = response.data
      if (!landingPayload) {
        setError('Campanha temporariamente indisponível.')
        return
      }

      setData(response)

      const donorMe = await landingFetchDonorMe(donorToken, landingPayload.campaign.id)
      writeLandingDonorToken(donorToken)

      const luckyNumbers = await landingFetchCampaignLuckyNumbers(
        donorToken,
        landingPayload.campaign.id
      )

      setDonor(donorMe)
      setNumbers(luckyNumbers)
      setProfileForm(buildProfileFormState(donorMe))

      setIsLoadingHistory(true)
      try {
        const history = await landingFetchRouletteHistory(
          donorToken,
          landingPayload.campaign.id
        )
        setRouletteHistory(history)
      } catch (historyErr) {
        console.error('Erro ao buscar histórico da roleta:', historyErr)
      } finally {
        setIsLoadingHistory(false)
      }
    } catch (err) {
      if (err instanceof ApiClientError) {
        clearLandingDonorSessionIfUnauthorized(err.status)
        setError(err.message)
      } else {
        setError('Não foi possível carregar sua área da campanha.')
      }
    } finally {
      setIsLoading(false)
    }
  }, [donorToken, slug])

  useEffect(() => {
    void loadAccountContext()
  }, [loadAccountContext])

  const campaign: LandingCampaign | null = data?.data?.campaign ?? null
  // Refreshes may receive the awarded prize before the visible wheel has stopped.
  // Freeze every prize surface, including the claim dialog on the Numbers tab.
  const visibleRouletteHistory =
    spinHistory &&
    spinHistory.campaignId === campaign?.id &&
    spinHistory.donorId === donor?.id
      ? spinHistory.history
      : rouletteHistory
  const purchaseScratch = usePurchaseScratch(
    campaign?.id,
    donor?.id,
    purchaseScratchRefreshKey
  )
  const donationOnly = isDonationOnlyCampaign(campaign)
  const {
    openPurchaseWidget,
    openPurchaseWidgetWithCoupon,
    openPurchaseWidgetForRanking,
    isWidgetLoading,
    widgetError,
  } = useLandingCampaignWidget(campaign, donor?.id)
  const salesOpen = isCampaignOpenForSales(campaign)
  const comprarDisabled = !campaign?.id || isWidgetLoading || !salesOpen

  useEffect(() => {
    initializeMetaPixel(campaign?.fb_pixel)
  }, [campaign?.fb_pixel])

  useEffect(() => {
    if (!campaign?.id) return
    let cancelled = false
    setPublicRoulette(undefined)
    void fetchPublicCampaignRoulette(campaign.id).then((result) => {
      if (!cancelled) setPublicRoulette(result)
    })
    return () => {
      cancelled = true
    }
  }, [campaign?.id])

  const refreshRouletteBalance = useCallback(() => {
    if (!campaign?.id || !donor?.id || !donorToken || publicRoulette == null) return
    const campaignId = campaign.id
    const donorId = donor.id
    const requestId = ++rouletteBalanceRequestRef.current
    void landingRouletteGetSpins(donorToken, campaignId)
      .then((value) => {
        if (requestId === rouletteBalanceRequestRef.current) {
          setRouletteBalance({ campaignId, donorId, value })
        }
      })
      .catch(() => {
        if (requestId === rouletteBalanceRequestRef.current) {
          setRouletteBalance((current) => ({
            campaignId,
            donorId,
            value:
              current?.campaignId === campaignId && current.donorId === donorId
                ? current.value
                : null,
            error: 'Não foi possível atualizar seus giros. Tente novamente.',
          }))
        }
      })
  }, [campaign?.id, donor?.id, donorToken, publicRoulette])

  useEffect(() => {
    refreshRouletteBalance()
  }, [refreshRouletteBalance])

  const handleRouletteBalanceChange = useCallback(
    (value: LandingRouletteSpinsResponse) => {
      if (!campaign?.id || !donor?.id) return
      rouletteBalanceRequestRef.current += 1
      setRouletteBalance({ campaignId: campaign.id, donorId: donor.id, value })
    },
    [campaign?.id, donor?.id]
  )

  const rouletteOpportunityCount = getRouletteOpportunityCount(
    rouletteBalance != null &&
      rouletteBalance.campaignId === campaign?.id &&
      rouletteBalance.donorId === donor?.id
      ? rouletteBalance.value
      : null
  )

  useEffect(() => {
    if (!focusRouleta || publicRoulette === undefined || publicRoulette === null) return
    const timeoutId = window.setTimeout(() => {
      setShowOpportunityModal(true)
      document
        .getElementById('campanha-roleta')
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 450)
    return () => window.clearTimeout(timeoutId)
  }, [focusRouleta, publicRoulette])

  const shareUrl = useMemo(() => {
    if (typeof window === 'undefined' || !slug) return ''
    return `${window.location.origin}/campanha/${slug}`
  }, [slug])

  const handleRouletteWin = useCallback(() => {
    if (!donorToken || !campaign?.id) return
    void landingFetchCampaignLuckyNumbers(donorToken, campaign.id)
      .then(setNumbers)
      .catch(() => {})
    void landingFetchRouletteHistory(donorToken, campaign.id)
      .then(setRouletteHistory)
      .catch(() => {})
  }, [donorToken, campaign?.id])

  const handleAchievementRewards = useCallback(
    (changed: boolean) => {
      if (!changed && !pendingScratchRefresh.current) return
      pendingScratchRefresh.current = false
      handleRouletteWin()
      refreshRouletteBalance()
    },
    [handleRouletteWin, refreshRouletteBalance]
  )

  useEffect(() => {
    let scheduled: number | null = null
    const refresh = () => {
      if (document.visibilityState !== 'visible' || scheduled !== null) return
      scheduled = window.requestAnimationFrame(() => {
        scheduled = null
        pendingScratchRefresh.current = true
        setPurchaseScratchRefreshKey((current) => current + 1)
        setAchievementsRefreshKey((current) => current + 1)
      })
    }
    window.addEventListener('focus', refresh)
    document.addEventListener('visibilitychange', refresh)
    return () => {
      if (scheduled !== null) window.cancelAnimationFrame(scheduled)
      window.removeEventListener('focus', refresh)
      document.removeEventListener('visibilitychange', refresh)
    }
  }, [])

  const handleUseCoupon = useCallback(
    async (result: LandingSpinResult) => {
      const coupon = buildPendingCouponFromSpinResult(result)
      if (!coupon) {
        throw new Error('invalid_coupon')
      }
      await openPurchaseWidgetWithCoupon(coupon)
    },
    [openPurchaseWidgetWithCoupon]
  )

  const institution: LandingInstitution | null = data?.data?.institution ?? null
  const institutionName = buildLandingInstitutionName(institution) || 'Campanha'
  const homeHref = resolveCampaignHomeHref(slug, campaign?.custom_domain, affiliateCode)

  const completionRate = useMemo(() => {
    if (!profileForm) return 0
    return getProfileCompletionRate(profileForm)
  }, [profileForm])
  const isProfileComplete = useMemo(
    () =>
      donor?.person_type != null &&
      getProfileCompletionRate(buildProfileFormState(donor)) === 100,
    [donor]
  )

  const purchasesCount = useMemo(
    () => groupLuckyNumbersByPurchase(numbers, '').length,
    [numbers]
  )

  const handleAvatarSaved = useCallback((avatar: AvatarConfig) => {
    setDonor((current) => (current ? { ...current, avatar } : current))
    setRankingRefreshKey((current) => current + 1)
    setAchievementsRefreshKey((current) => current + 1)
  }, [])

  const handleMascotSaved = useCallback((mascot: MascotConfig) => {
    setDonor((current) => (current ? { ...current, mascot } : current))
    setRankingRefreshKey((current) => current + 1)
  }, [])

  const handleUnlockedBadgesChange = useCallback(
    (campaignId: number, ids: LandingAchievementId[]) => {
      setEarnedBadges({ campaignId, ids })
    },
    []
  )

  const openAvatarEditor = useCallback(() => {
    setAvatarOpenSignal((current) => current + 1)
  }, [])

  const handleAvatarBadgeClick = useCallback(() => {
    if (avatarBadgeUnlocked && isProfileComplete)
      setAvatarDetailsSignal((current) => current + 1)
    else openAvatarEditor()
  }, [avatarBadgeUnlocked, isProfileComplete, openAvatarEditor])

  const updateProfileField = useCallback(
    <K extends keyof ProfileFormState>(field: K, value: ProfileFormState[K]) => {
      setProfileForm((current) => (current ? { ...current, [field]: value } : current))
    },
    []
  )

  const updatePasswordField = useCallback(
    (field: keyof PasswordFormState, value: string) => {
      setPasswordForm((current) => ({ ...current, [field]: value }))
    },
    []
  )

  const handleSaveProfile = useCallback(async () => {
    if (!donorToken || !profileForm) return

    const birthDate = birthDateToIso(profileForm.birth_date)
    if (profileForm.birth_date && !birthDate) {
      setProfileError('Informe uma data de nascimento válida no formato DD/MM/AAAA.')
      return
    }

    setIsSavingProfile(true)
    setProfileError(null)
    setProfileSuccess(null)

    try {
      const documentDigits = onlyDigits(profileForm.document)
      const updatedDonor = await landingUpdateDonorProfile(donorToken, {
        person_type: profileForm.person_type,
        full_name:
          profileForm.person_type === 'pf' ? profileForm.full_name.trim() || null : null,
        corporate_name:
          profileForm.person_type === 'pj'
            ? profileForm.corporate_name.trim() || null
            : null,
        document_cpf: profileForm.person_type === 'pf' ? documentDigits || null : null,
        document_cnpj: profileForm.person_type === 'pj' ? documentDigits || null : null,
        email: profileForm.email.trim().toLowerCase() || null,
        phone: onlyDigits(profileForm.phone) || null,
        gender: profileForm.gender.trim() || null,
        birth_date: birthDate,
        website: profileForm.website.trim() || null,
        address: {
          street: profileForm.street.trim() || null,
          number: profileForm.number.trim() || null,
          complement: profileForm.complement.trim() || null,
          neighborhood: profileForm.neighborhood.trim() || null,
          city: profileForm.city.trim() || null,
          state: profileForm.state.trim() || null,
          postal_code: onlyDigits(profileForm.postal_code) || null,
        },
      })

      // Profile and appearance saves own different fields; a slower profile
      // response must not replace an avatar or mascot just saved in its editor.
      setDonor((current) => ({
        ...updatedDonor,
        id: current?.id ?? updatedDonor.id,
        donor_id: current?.donor_id ?? updatedDonor.donor_id,
        avatar: current?.avatar ?? updatedDonor.avatar,
        mascot: current?.mascot ?? updatedDonor.mascot,
      }))
      setProfileForm(buildProfileFormState(updatedDonor))
      setProfileSuccess('Seus dados foram atualizados com sucesso.')
      setAchievementsRefreshKey((current) => current + 1)
    } catch (err) {
      setProfileError(
        err instanceof ApiClientError
          ? flattenApiError(err)
          : 'Não foi possível salvar seus dados.'
      )
    } finally {
      setIsSavingProfile(false)
    }
  }, [donorToken, profileForm])

  const handleChangePassword = useCallback(async () => {
    if (!donorToken) return
    if (passwordForm.new_password.length < 8) {
      setSecurityError('A nova senha deve ter no mínimo 8 caracteres.')
      return
    }
    if (passwordForm.new_password !== passwordForm.new_password_confirmation) {
      setSecurityError('A confirmação da nova senha não confere.')
      return
    }

    setIsSavingPassword(true)
    setSecurityError(null)
    setSecuritySuccess(null)

    try {
      const message = await landingChangeDonorPassword(donorToken, passwordForm)
      setPasswordForm(createEmptyPasswordForm())
      setSecuritySuccess(message || 'Senha atualizada com sucesso.')
    } catch (err) {
      setSecurityError(
        err instanceof ApiClientError
          ? flattenApiError(err)
          : 'Não foi possível atualizar sua senha.'
      )
    } finally {
      setIsSavingPassword(false)
    }
  }, [donorToken, passwordForm])

  const saveButtonClassName = `${purchaseButtonStyles.submitButton} ${purchaseButtonStyles.comprarButtonWide}`

  const buyAction =
    campaign && !salesOpen ? (
      <LandingSalesClosedNotice campaign={campaign} visualVariant="light" />
    ) : (
      <>
        {widgetError ? <p className={styles.error}>{widgetError}</p> : null}
        <button
          type="button"
          className={`${purchaseButtonStyles.submitButton} ${purchaseButtonStyles.buttonWithIcon} ${purchaseButtonStyles.comprarButtonWide} ${styles.purchaseWidgetButton}`}
          onClick={openPurchaseWidget}
          disabled={comprarDisabled}
        >
          <Icon
            icon={isWidgetLoading ? 'mdi:loading' : 'mdi:cart-plus'}
            width={22}
            height={22}
            className={isWidgetLoading ? purchaseButtonStyles.buttonIconSpin : undefined}
            aria-hidden
          />
          <span>
            {isWidgetLoading
              ? 'Abrindo...'
              : donationOnly
                ? 'Fazer nova compra'
                : 'Comprar mais números'}
          </span>
        </button>
      </>
    )

  const unavailableContent = (
    <div className={styles.statusCard}>
      <h1 className={styles.statusTitle}>Área indisponível</h1>
      <p className={styles.error}>{error || 'Não foi possível abrir sua conta.'}</p>
      <button
        type="button"
        className={`${saveButtonClassName} ${styles.purchaseWidgetButton}`}
        onClick={redirectToAccount}
      >
        Voltar para conta
      </button>
    </div>
  )

  if (isLoading) return <LandingProfileSkeleton />

  if (error || !campaign || !institution || !donorToken || !donor || !profileForm) {
    return (
      <LandingProfileShell
        campaign={campaign}
        institution={institution}
        homeHref={homeHref}
      >
        {unavailableContent}
      </LandingProfileShell>
    )
  }

  return (
    <LandingProfileShell
      campaign={campaign}
      institution={institution}
      homeHref={homeHref}
      onLogout={() => void handleLogout()}
      isLoggingOut={isLoggingOut}
      logoutError={logoutError}
    >
      <LandingProfileQuestionnaire
        key={`${campaign.id}:${donor.id}`}
        campaignId={campaign.id}
        achievementsReady={achievementsReady}
      />
      <LandingProfileWorkspace
        shop={
          institution.shop_enabled
            ? { campaignId: campaign.id, institutionName }
            : undefined
        }
        key={`workspace:${campaign.id}:${donor.id}`}
        aiCampaignId={campaign.id}
        onAiPurchaseConfirmed={handleRouletteWin}
        scratch={{
          visible:
            purchaseScratch.state?.visible === true || Boolean(purchaseScratch.error),
          count:
            (purchaseScratch.state?.available ?? 0) +
            (purchaseScratch.state?.pending ?? 0),
          onOpen: () => void purchaseScratch.start(),
        }}
        scratchModal={(onViewNumbers) => (
          <PurchaseScratchModal
            scratch={purchaseScratch}
            onChanged={handleRouletteWin}
            onUseCoupon={handleUseCoupon}
            onViewNumbers={onViewNumbers}
            salesOpen={salesOpen}
          />
        )}
        homeHref={homeHref}
        rouletteOpportunityCount={rouletteOpportunityCount}
        requestedTab={focusRouleta && publicRoulette != null ? 'roleta' : undefined}
        identity={(onCompleteProfile, onViewNumbers, onViewAchievements) => (
          <>
            <LandingProfileIdentity
              donor={donor}
              coverUrl={resolveLandingAccountBackdropUrl(campaign)}
              completionRate={completionRate}
              numbersCount={numbers.length}
              purchasesCount={purchasesCount}
              earnedBadgeIds={
                earnedBadges?.campaignId === campaign.id ? earnedBadges.ids : []
              }
              onAvatarSaved={handleAvatarSaved}
              onMascotSaved={handleMascotSaved}
              onMascotGameClosed={() => {
                setRankingRefreshKey((current) => current + 1)
                setAchievementsRefreshKey((current) => current + 1)
              }}
              onVeterinaryPaid={() => {
                pendingScratchRefresh.current = true
                setPurchaseScratchRefreshKey((current) => current + 1)
                setAchievementsRefreshKey((current) => current + 1)
                setRankingRefreshKey((current) => current + 1)
              }}
              campaignId={campaign.id}
              avatarOpenSignal={avatarOpenSignal}
              avatarBadgeUnlocked={avatarBadgeUnlocked}
              avatarBadgeEnabled={avatarBadgeEnabled}
              isProfileComplete={isProfileComplete}
              onAvatarBadgeClick={handleAvatarBadgeClick}
              onCompleteProfile={onCompleteProfile}
              onViewNumbers={onViewNumbers}
              onViewAchievements={onViewAchievements}
            />
            {campaign && !salesOpen ? (
              <LandingSalesClosedNotice campaign={campaign} visualVariant="light" />
            ) : (
              <div className={styles.buyPrompt}>
                {widgetError ? <p className={styles.error}>{widgetError}</p> : null}
                <button
                  type="button"
                  className={styles.buyPromptButton}
                  onClick={openPurchaseWidget}
                  disabled={comprarDisabled}
                >
                  <Icon
                    icon={isWidgetLoading ? 'mdi:loading' : 'mdi:cart-plus'}
                    className={
                      isWidgetLoading
                        ? `${styles.buyPromptIcon} ${styles.buyPromptIconSpin}`
                        : styles.buyPromptIcon
                    }
                    aria-hidden
                  />
                  <span>
                    {isWidgetLoading
                      ? 'Abrindo...'
                      : donationOnly
                        ? 'Fazer nova compra'
                        : 'Comprar Mais Números'}
                  </span>
                </button>
              </div>
            )}
            <ParticipantShareButton
              key={`${campaign.id}:${donor.id}`}
              campaign={campaign}
              institution={institution}
              emphasized
            />
          </>
        )}
        achievements={(onCompleteProfile, onViewNumbers, onGoToRoulette) => (
          <LandingProfileAchievements
            onGoToRoulette={publicRoulette != null ? onGoToRoulette : undefined}
            key={`${campaign.id}:${donor.id}`}
            campaignId={campaign.id}
            donorId={donor.id}
            salesOpen={salesOpen}
            isPurchaseLoading={isWidgetLoading}
            donationOnly={donationOnly}
            isProfileComplete={isProfileComplete}
            onPurchase={openPurchaseWidget}
            onCompleteProfile={onCompleteProfile}
            onPersonalizeAvatar={openAvatarEditor}
            onAvatarBadgeStatus={handleAvatarBadgeStatus}
            onUnlockedBadgesChange={handleUnlockedBadgesChange}
            openAvatarDetailsSignal={avatarDetailsSignal}
            refreshSignal={achievementsRefreshKey}
            onViewPrizes={onViewNumbers}
            onCelebrationReady={setAchievementsReady}
            onRewardsChanged={handleAchievementRewards}
            onScratchCardChanged={() => {
              pendingScratchRefresh.current = true
              setAchievementsRefreshKey((current) => current + 1)
              setRankingRefreshKey((current) => current + 1)
            }}
          />
        )}
        ranking={
          <LandingProfileTopDonors
            campaignId={campaign.id}
            refreshKey={rankingRefreshKey}
            campaign={campaign}
            salesOpen={salesOpen}
            purchaseLoading={isWidgetLoading}
            onPurchaseForRanking={openPurchaseWidgetForRanking}
          />
        }
        numbers={
          <>
            <LandingScratchPrizes
              prizes={visibleRouletteHistory}
              onUseCoupon={handleUseCoupon}
              salesOpen={salesOpen}
              isLoading={isWidgetLoading}
            />
            {donorToken && (
              <LandingRouletteClaims
                autoOpen={!purchaseScratch.open}
                key={`${campaign.id}:${donor.id}`}
                prizes={visibleRouletteHistory}
                token={donorToken}
                campaignId={campaign.id}
                onClaimed={(prize) => {
                  setRouletteHistory((items) =>
                    items.map((item) => (item.id === prize.id ? prize : item))
                  )
                  handleRouletteWin()
                }}
              />
            )}
            <LandingProfileNumbers
              donationOnly={donationOnly}
              query={numbersQuery}
              numbers={numbers}
              onQueryChange={setNumbersQuery}
              buyAction={buyAction}
            />
          </>
        }
        roulette={
          publicRoulette != null && donorToken ? (
            <LandingProfileRoulette
              externalBalance={
                rouletteBalance?.campaignId === campaign.id &&
                rouletteBalance.donorId === donor.id
                  ? rouletteBalance.value
                  : null
              }
              externalBalanceError={
                rouletteBalance?.campaignId === campaign.id &&
                rouletteBalance.donorId === donor.id
                  ? rouletteBalance.error
                  : null
              }
              onRetryBalance={refreshRouletteBalance}
              campaign={campaign}
              institution={institution}
              donorToken={donorToken}
              publicRoulette={publicRoulette}
              shareUrl={shareUrl}
              salesOpen={salesOpen}
              isWidgetLoading={isWidgetLoading}
              isLoadingHistory={isLoadingHistory}
              rouletteHistory={visibleRouletteHistory.filter(
                (prize) =>
                  !['scratch_card', 'achievement', 'purchase_scratch_card'].includes(
                    prize.prize_payload?.source ?? ''
                  )
              )}
              showOpportunityModal={showOpportunityModal}
              onWin={() => {
                handleRouletteWin()
                setAchievementsRefreshKey((current) => current + 1)
              }}
              onSpinningChange={(spinning) =>
                setSpinHistory(
                  spinning
                    ? {
                        campaignId: campaign.id,
                        donorId: donor.id,
                        history: rouletteHistory,
                      }
                    : null
                )
              }
              onBalanceChange={handleRouletteBalanceChange}
              onUseCoupon={handleUseCoupon}
              onBuyClick={openPurchaseWidget}
              onCloseOpportunityModal={() => setShowOpportunityModal(false)}
              onGoToRoulette={() => {
                setShowOpportunityModal(false)
                document
                  .getElementById('campanha-roleta')
                  ?.scrollIntoView({ behavior: 'smooth' })
              }}
            />
          ) : null
        }
        cadastro={
          <LandingProfileCadastro
            profileForm={profileForm}
            isSavingProfile={isSavingProfile}
            profileError={profileError}
            profileSuccess={profileSuccess}
            onProfileFieldChange={updateProfileField}
            onSaveProfile={() => void handleSaveProfile()}
            saveProfileButtonClassName={saveButtonClassName}
          />
        }
        security={
          <>
            <LandingProfileSecurity
              profileForm={profileForm}
              passwordForm={passwordForm}
              isSavingPassword={isSavingPassword}
              securityError={securityError}
              securitySuccess={securitySuccess}
              institutionName={institutionName}
              isGuest={donor.is_guest}
              onPasswordFieldChange={updatePasswordField}
              onChangePassword={() => void handleChangePassword()}
              savePasswordButtonClassName={saveButtonClassName}
            />
          </>
        }
      />
    </LandingProfileShell>
  )
}
